import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import O_HeaderResponsive from '@/components/O_Organisms/O_Header/O_HeaderResponsive';

import styles from './MyCapsulesPage.module.css';
import M_SelectPicker from '@/components/M_Molecules/M_SelectPicker/M_SelectPicker';
import M_MyCapsuleCard from '@/components/M_Molecules/M_MyCapsuleCard/M_MyCapsuleCard';
import M_CapsuleCard from '@/components/M_Molecules/M_CapsuleCard/M_CapsuleCard';
import { W_MasonryGrid } from '@/components/W_Wrappers/W_MasonryGrid/W_MasonryGrid';
import {
  useCapsules,
  useActivateCapsule,
  usePosthumousApprove,
} from '@/hooks/useCapsules';
import W_Loader from '@/components/W_Wrappers/W_Loader/W_Loader';
import W_CapsulesEmptyState from '@/components/W_Wrappers/W_CapsulesEmptyState/W_CapsulesEmptyState';
import { PATHS } from '@/constants/paths';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';

const TAB_KEYS = {
  CLOSED: 'closed',
  OPENED: 'opened',
};

export default function MyCapsulesPage() {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const { user } = useAuth();
  const userId = user?.id || '';
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedOption, setSelectedOption] = useState(TAB_KEYS.CLOSED);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const activateCapsuleMutation = useActivateCapsule();

  useEffect(() => {
    const stateNotification = (location.state as { notification?: string })
      ?.notification;
    if (stateNotification === 'createSuccess') {
      setNotification({
        type: 'success',
        message: getTranslation(
          language,
          'myCapsules.notifications.createSuccess'
        ),
      });
      navigate(location.pathname, { replace: true, state: {} });
    } else if (stateNotification === 'deleteSuccess') {
      setNotification({
        type: 'success',
        message: getTranslation(
          language,
          'myCapsules.notifications.deleteSuccess'
        ),
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, language]);

  const posthumousApproveMutation = usePosthumousApprove();

  const isOpened = selectedOption === TAB_KEYS.OPENED;
  const {
    data: capsulesData,
    loading,
    refetch,
  } = useCapsules(
    isOpened
      ? {
          authorId: userId,
          isOpened: true,
        }
      : {
          isClosed: true,
        }
  );

  /** Пока идёт запрос после смены вкладки, не показываем кэш от предыдущего фильтра */
  const allCapsules = loading ? [] : capsulesData?.data?.capsules || [];

  const capsules = allCapsules.filter((capsule: any) => {
    const capsuleIsOpened = capsule.meta?.isOpened === true;

    if (isOpened) {
      return capsuleIsOpened;
    } else {
      return !capsuleIsOpened;
    }
  });

  const handleSelect = (option: string) => {
    setSelectedOption(option);
  };

  const handleCardClick = (capsule: { id: string; author: { id: string } }) => {
    const isOwner = String(capsule.author.id) === userId;
    if (!isOwner) return;
    navigate(PATHS.CAPSULE_EDIT_BY_ID(capsule.id));
  };

  const handleActivate = async (capsuleId: string) => {
    try {
      await activateCapsuleMutation.mutate(capsuleId);
      await refetch();
      setNotification({
        type: 'success',
        message: t('myCapsules.notifications.activateSuccess'),
      });
    } catch {
      setNotification({
        type: 'error',
        message: t('myCapsules.notifications.activateError'),
      });
    }
  };

  const handlePublish = async (capsuleId: string) => {
    try {
      await posthumousApproveMutation.mutate(capsuleId);
      await refetch();
      setNotification({
        type: 'success',
        message: t('myCapsules.notifications.publishSuccess'),
      });
    } catch {
      setNotification({
        type: 'error',
        message: t('myCapsules.notifications.publishError'),
      });
    }
  };

  if (loading) {
    return (
      <div className={styles.loader_page}>
        <W_Loader isLoading />
        {notification && (
          <A_Notification
            type={notification.type}
            message={notification.message}
            isVisible
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <O_HeaderResponsive />
      <div className={styles.content}>
        <h1 className={styles.title}>{t('myCapsules.title')}</h1>
        <div className={styles.capsules_wrapper}>
          <M_SelectPicker
            options={[t('myCapsules.closed'), t('myCapsules.opened')]}
            selectedOption={
              selectedOption === TAB_KEYS.CLOSED
                ? t('myCapsules.closed')
                : t('myCapsules.opened')
            }
            onSelect={option => {
              const isClosed = option === t('myCapsules.closed');
              handleSelect(isClosed ? TAB_KEYS.CLOSED : TAB_KEYS.OPENED);
            }}
            background='white'
          />
          {selectedOption === TAB_KEYS.CLOSED ? (
            <div className={styles.capsules_list}>
              {capsules.length === 0 ? (
                <W_CapsulesEmptyState hasButton />
              ) : (
                capsules.map((capsule: any) => {
                  const coauthorAvatars = (capsule.privacy.coauthors || [])
                    .map((a: any) => a.avatar)
                    .filter(Boolean) as string[];
                  const avatars = [
                    capsule.author.avatar || '',
                    ...coauthorAvatars,
                  ].filter(Boolean);
                  const coauthorsCount =
                    capsule.privacy.coauthors?.length ||
                    capsule.privacy.allowedUserIds?.length ||
                    0;
                  return (
                    <M_MyCapsuleCard
                      key={capsule.id}
                      id={capsule.id}
                      content={capsule.content}
                      isActivated={capsule.meta?.isOpened}
                      openedAt={capsule.meta?.firstOpenedAt}
                      scheduledAt={capsule.meta?.scheduledAt}
                      title={capsule.title}
                      avatars={avatars}
                      coauthors={coauthorsCount}
                      ownerName={capsule.author.name}
                      isMyCapsule={capsule.author.id === userId}
                      isLifeCapsule={
                        capsule.meta?.activationType === 'custodian'
                      }
                      isGuardian={
                        capsule.meta?.activationType === 'custodian' &&
                        capsule.author.id !== userId
                      }
                      onClick={
                        String(capsule.author.id) === userId
                          ? () => handleCardClick(capsule)
                          : undefined
                      }
                      onActivate={() => handleActivate(capsule.id)}
                      onPublish={() => handlePublish(capsule.id)}
                    />
                  );
                })
              )}
            </div>
          ) : capsules.length === 0 ? (
            <div className={styles.empty_area}>
              <W_CapsulesEmptyState hasButton />
            </div>
          ) : (
            <W_MasonryGrid fourColumns={true}>
              {capsules.map((capsule: any) => {
                const coauthorAvatars = (capsule.privacy.coauthors || [])
                  .map((a: any) => a.avatar)
                  .filter(Boolean) as string[];
                const avatars = [
                  capsule.author.avatar || '',
                  ...coauthorAvatars,
                ].filter(Boolean);
                const coauthorsCount =
                  capsule.privacy.coauthors?.length ||
                  capsule.privacy.allowedUserIds?.length ||
                  0;
                const firstImage = capsule.content?.find(
                  (item: any) =>
                    item?.type === 'image' || item?.type === 'video'
                );
                const imageUrl =
                  firstImage?.previewUrl ||
                  firstImage?.url ||
                  capsule.imagePreview ||
                  capsule.image ||
                  '';

                return (
                  <M_CapsuleCard
                    key={capsule.id}
                    id={capsule.id}
                    image={imageUrl}
                    title={capsule.title}
                    avatars={avatars}
                    coauthors={coauthorsCount}
                    commentsCount={capsule.engagement?.comments || 0}
                    isMyCapsule={capsule.author.id === userId}
                    ownerName={capsule.author.name}
                    ownerId={capsule.author.id}
                  />
                );
              })}
            </W_MasonryGrid>
          )}
        </div>
      </div>
      {notification && (
        <A_Notification
          type={notification.type}
          message={notification.message}
          isVisible
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
